import { Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { AdminCardConfig } from '@/config/adminCards';
import { cn } from '@/lib/utils';

interface AdminCardProps {
  card: AdminCardConfig;
  isFavorite: boolean;
  onToggleFavorite: (cardId: string) => void;
}

export function AdminCard({
  card,
  isFavorite,
  onToggleFavorite,
}: AdminCardProps) {
  const navigate = useNavigate();

  return (
    <Card className="relative flex h-full flex-col">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 h-8 w-8"
        aria-pressed={isFavorite}
        aria-label={
          isFavorite
            ? `Remove ${card.title} from the menu`
            : `Add ${card.title} to the menu`
        }
        title={isFavorite ? 'Remove from menu' : 'Add to menu'}
        onClick={() => onToggleFavorite(card.id)}
      >
        <Star
          className={cn(
            'h-5 w-5',
            isFavorite
              ? 'fill-yellow-400 text-yellow-500'
              : 'text-gray-400 hover:text-yellow-500',
          )}
        />
      </Button>

      <CardHeader className="pr-12">
        <CardTitle className="text-lg font-semibold">{card.title}</CardTitle>
        <CardDescription className="text-gray-600">
          {card.description}
        </CardDescription>
      </CardHeader>

      <CardContent className="mt-auto space-y-2">
        {card.actions.map(({ label, path, icon: Icon, variant }) => (
          <Button
            key={path}
            variant={variant}
            className={cn(
              'w-full',
              variant === 'outline' && 'bg-white hover:bg-gray-200',
            )}
            onClick={() => navigate(path)}
          >
            <Icon className="mr-2 h-4 w-4" />
            {label}
          </Button>
        ))}
      </CardContent>
    </Card>
  );
}
